import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AuthCardLayout } from '@/components/AuthCardLayout';
import { apiClient } from '@/lib/api-client';
import { MIN_SENHA_LENGTH } from '@/lib/senha';
import { useAuth } from '@/contexts/AuthContext';

export default function RedefinirSenha() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  // O token é lido UMA vez e logo sai da URL (histórico, Referer, Sentry e prints não ficam com ele).
  // Consequência: recarregar a página depois disso pede um novo link.
  const [token] = useState(() => searchParams.get('token') ?? '');
  // `convite=1`: link do cadastro inteligente, para quem nunca teve senha ("Crie sua senha", não "redefinir").
  const [convite] = useState(() => searchParams.get('convite') === '1');
  useEffect(() => {
    if (searchParams.has('token')) navigate({ pathname: location.pathname, search: convite ? '?convite=1' : '' }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const { logout } = useAuth();
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (novaSenha.length < MIN_SENHA_LENGTH) {
      setError(`A senha deve ter pelo menos ${MIN_SENHA_LENGTH} caracteres`);
      return;
    }
    if (novaSenha !== confirmarSenha) {
      setError('As senhas não conferem');
      return;
    }
    setIsLoading(true);
    try {
      await apiClient.post('/auth/redefinir-senha', { token, novaSenha });
      // Encerra qualquer sessão antiga neste navegador; senão /login redireciona direto ao dashboard.
      logout();
      toast.success(convite ? 'Senha criada. Faça login para entrar no Kora.' : 'Senha redefinida com sucesso. Faça login com a nova senha.');
      navigate('/login');
    } catch (err) {
      const mensagem = err instanceof Error ? err.message : 'Erro ao redefinir senha';
      // Convite vencido: a saída é o "Esqueci minha senha", não "solicitar uma redefinição".
      setError(convite && /inválido ou expirado/.test(mensagem)
        ? 'Este convite expirou ou já foi usado. Use "Esqueci minha senha" na tela de login com o seu e-mail.'
        : mensagem);
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return (
      <AuthCardLayout
        titulo="Link inválido"
        descricao={convite
          ? 'Este convite não é válido, está incompleto ou a página foi recarregada. Peça um novo link em "Esqueci minha senha".'
          : 'Este link de redefinição não é válido, está incompleto ou a página foi recarregada. Peça um novo link.'}
      >
        <Button asChild className="h-11 w-full">
          <Link to="/esqueci-senha">Solicitar novo link</Link>
        </Button>
      </AuthCardLayout>
    );
  }

  return (
    <AuthCardLayout
      titulo={convite ? 'Crie sua senha' : 'Redefinir senha'}
      descricao={convite ? 'Boas-vindas ao Kora! Escolha a senha que você vai usar para entrar.' : 'Escolha uma nova senha para sua conta'}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <Alert variant="destructive" className="animate-fade-in">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <div className="space-y-2">
          <Label htmlFor="novaSenha">{convite ? 'Senha' : 'Nova senha'}</Label>
          <PasswordInput id="novaSenha" value={novaSenha} onChange={(e) => setNovaSenha(e.target.value)} required className="h-11" autoComplete="new-password" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirmarSenha">{convite ? 'Confirmar senha' : 'Confirmar nova senha'}</Label>
          <PasswordInput id="confirmarSenha" value={confirmarSenha} onChange={(e) => setConfirmarSenha(e.target.value)} required className="h-11" autoComplete="new-password" />
        </div>
        <Button type="submit" className="h-11 w-full font-semibold" disabled={isLoading}>
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Salvando...
            </>
          ) : (
            convite ? 'Criar senha' : 'Redefinir senha'
          )}
        </Button>
      </form>
    </AuthCardLayout>
  );
}
